import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import {
  CreateOrgParams,
  UpdateOrgParams,
  Org,
} from "../../services/organization.ts";

interface OrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateOrgParams | UpdateOrgParams) => Promise<void>;
  organization?: Org | null; // 如果是编辑模式，传入现有的组织数据
  loading?: boolean;
}

const OrganizationModal: React.FC<OrganizationModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  organization,
  loading = false,
}) => {
  const isEditMode = !!organization;

  const [form, setForm] = useState<CreateOrgParams>({
    name: "",
    description: "",
    adminEmail: "",
    adminFirstName: "",
    adminLastName: "",
  });

  // 当组织数据变化时，更新表单
  useEffect(() => {
    if (organization) {
      setForm({
        name: organization.name,
        description: organization.description,
        adminEmail: organization.owner?.email || "",
        adminFirstName: organization.owner?.firstName || "",
        adminLastName: organization.owner?.lastName || "",
      });
    } else {
      // 重置表单
      setForm({
        name: "",
        description: "",
        adminEmail: "",
        adminFirstName: "",
        adminLastName: "",
      });
    }
  }, [organization]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isEditMode) {
      // 编辑模式：移除email字段
      const updateData: UpdateOrgParams = {
        name: form.name,
        description: form.description,
        adminFirstName: form.adminFirstName,
        adminLastName: form.adminLastName,
      };
      await onSubmit(updateData);
    } else {
      // 新建模式：包含所有字段
      await onSubmit(form);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEditMode ? "Edit Customer" : "Create New Customer"}
          </h2>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Customer Name *
              </label>
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={form.description}
                onChange={(e) =>
                  setForm({
                    ...form,
                    description: e.target.value,
                  })
                }
                disabled={loading}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Owner Email {!isEditMode && "*"}
              </label>
              <input
                type="email"
                required={!isEditMode}
                value={form.adminEmail}
                onChange={(e) =>
                  setForm({
                    ...form,
                    adminEmail: e.target.value,
                  })
                }
                disabled={loading || isEditMode}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  isEditMode ? "bg-gray-100 text-gray-500" : ""
                } disabled:opacity-50`}
                placeholder={
                  isEditMode
                    ? "Email cannot be modified"
                    : "Enter email address"
                }
              />
              {isEditMode && (
                <p className="text-xs text-gray-500 mt-1">
                  Email address cannot be modified after organization creation
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  required
                  value={form.adminFirstName}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      adminFirstName: e.target.value,
                    })
                  }
                  disabled={loading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  required
                  value={form.adminLastName}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      adminLastName: e.target.value,
                    })
                  }
                  disabled={loading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {loading
                ? isEditMode
                  ? "Updating..."
                  : "Creating..."
                : isEditMode
                ? "Update Customer"
                : "Create Customer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrganizationModal;
